import type { WhiteboardTranslations } from "./translations";

export const VI_TRANSLATIONS: WhiteboardTranslations = {
  workspace: {
    cancelLabel: "Hủy",
    syncingSession: "Đang đồng bộ phiên học...",
    initializingBoard: "Đang khởi tạo bảng trắng...",
    clearSlideModalTitle: "Xóa toàn bộ nét vẽ trên trang này?",
    clearSlideModalDescription:
      "Hành động này sẽ xóa toàn bộ các nét vẽ và công cụ trên trang hiện tại.",
    clearSlideConfirmLabel: "Xóa trang này",
    slideClearedToastTitle: "Đã xóa trang",
    slideClearedToastDesc: "Toàn bộ nét vẽ trên trang hiện tại đã được xóa.",
    slowNetworkAria: "Thông báo kết nối mạng chậm",
    slowNetworkTitle: "Kết nối mạng chậm",
    slowNetworkDesc:
      "— Đang đồng bộ phiên làm việc trực tuyến. Bạn đang làm việc ở chế độ bản nháp cục bộ.",
    retrySyncButton: "Thử đồng bộ lại",
    toolErrorTitle: "Bảng trắng đã gặp sự cố",
    toolErrorFallback: "Đã xảy ra lỗi không mong muốn khi vận hành công cụ này.",
    toolReloadButton: "Tải lại bảng trắng",
    dismissToastAria: "Đóng thông báo",
  },
  hud: {
    appMenuAriaLabel: "Menu ứng dụng",
    boardMenuAriaLabel: "Menu bảng trắng",
    exportPng: "Xuất hình ảnh",
    exportPdf: "Xuất PDF",
    pdfScopeAriaLabel: "Phạm vi xuất PDF",
    pdfScopeAll: "Toàn bộ bài giảng",
    pdfScopeTab: "Tab hiện tại",
    pdfScopeSlide: "Slide hiện tại",
    findOnCanvas: "Tìm trên bảng",
    help: "Trợ giúp",
    clearSlide: "Xóa bảng",
    paperStyleSectionTitle: "Kiểu giấy",
    strokeSmoothingSectionTitle: "Làm mượt nét bút",
    canvasBgSectionTitle: "Nền bảng",
    canvasBgSwatchAria: (color: string) => `Nền bảng ${color}`,
    customBgColorAria: "Màu nền tùy chỉnh",
    paperPresets: {
      blank: "Bảng trắng",
      "grid-small": "Ô ly nhỏ (Toán)",
      "grid-large": "Ô ly lớn",
      dots: "Ma trận điểm",
      lined: "Giấy kẻ ngang",
      tianzige: "Điền tự cách (田字格)",
      mizige: "Mễ tự cách (米字格)",
      "pinyin-tianzige": "Pinyin + Điền tự cách",
    },
    prevSlideAria: "Trang trước",
    nextSlideAria: "Trang tiếp theo",
    addSlideAria: "Thêm trang mới",
    duplicateSlideAria: "Nhân bản trang",
    duplicateSlideTitle: "Nhân bản trang hiện tại",
    deleteSlideAria: "Xóa trang hiện tại",
    confirmDeleteSlideAria: "Xác nhận xóa trang",
    confirmDeleteSlideLabel: "Xác nhận xóa?",
    cancelDeleteSlideAria: "Hủy xóa trang",
    cancelLabel: "Hủy",
    drawingToolbarAria: "Thanh công cụ vẽ và học liệu",
    pointerSelectionLabel: "Move",
    pointerHandLabel: "Hand tool",
    shapesMenuAria: "Chọn hình khối",
    shapeTools: {
      rectangle: "Hình vuông",
      diamond: "Hình thoi",
      ellipse: "Hình tròn",
      arrow: "Mũi tên",
      line: "Đường thẳng",
    },
    freedrawAria: "Bút vẽ",
    freedrawTitle: "Bút vẽ tự do (P)",
    textAria: "Văn bản",
    textTitle: "Văn bản (T)",
    eraserAria: "Tẩy",
    eraserTitle: "Tẩy (E)",
    insertMenuTriggerAria: "Chèn hình ảnh, tài liệu và video",
    insertMenuTriggerTitle: "Chèn ảnh, PDF hoặc Video",
    insertPopoverAria: "Chèn học liệu và đa phương tiện",
    insertImageLabel: "Hình ảnh, video",
    pinyinAssistantTrigger: "Trợ lý Pinyin & Chữ Hán",
    stemTriggerAria: "Công cụ STEM, Toán học và Thư viện hình",
    stemTriggerTitle: "Công thức LaTeX, Mô phỏng & Thư viện STEM",
    stemPopoverAria: "Công cụ Toán, Mô phỏng và Thư viện STEM",
    stemLibraryTrigger: "Thư viện hình STEM",
    stemAllSubjects: "Tất cả",
    stemGenerateSectionTitle: "Tạo theo yêu cầu",
    stemSidebarTitle: "Thư viện",
    stemSidebarCloseAria: "Đóng thư viện",
    stemCategoryLabels: {
      physics: { label: "Vật lý & Mạch điện", description: "Mạch điện, Quang học, Cơ học" },
      math: { label: "Toán & Hệ trục 3D", description: "Hình học 2D/3D, Hệ trục, Đồ thị" },
      chemistry: { label: "Hoá học", description: "Dụng cụ thí nghiệm, Cấu trúc phân tử" },
      cs: { label: "Tin học & Lưu đồ", description: "Flowchart, Mạng, Kiến trúc hệ thống" },
      chinese: {
        label: "Tiếng Trung (Điền tự cách / Nét chữ)",
        description: "Ô luyện chữ, 8 nét cơ bản, kết cấu chữ Hán & thanh điệu",
      },
    },
    latexTrigger: "LaTex",
    simulationTrigger: "Biểu đồ & hình học",
    youtubeTrigger: "Liên kết Youtube",
    laserAria: "Bút Laser",
    laserTitle: "Bút Laser (K)",
    cursorChatTriggerAria: "Chat trực tiếp trên con trỏ",
    cursorChatPlaceholder: "Nhập tin nhắn trên con trỏ...",
    cursorChatHint: "Enter để ghim · Esc để đóng",
    remoteHostBadge: "Giáo viên",
    remoteOverflowBadge: (count: number) => `+${count} người khác`,
    teacherPermissionsAria: "Cấu hình tương tác của học viên",
    screenFollowYou: "Theo màn bạn",
    screenFreeMove: "Tự do di chuyển",
    screenFollowingTeacher: "Đang theo giáo viên",
    drawCustom: "Tương tác",
    drawViewOnly: "Chỉ xem",
    undoAria: "Hoàn tác",
    undoTitle: "Hoàn tác (Ctrl+Z)",
    redoAria: "Làm lại",
    redoTitle: "Làm lại (Ctrl+Y)",
    zoomOutAria: "Thu nhỏ",
    zoomInAria: "Phóng to",
    zoomMenuAria: "Tùy chọn khung nhìn",
    zoomMenuHeader: (percent: number) => `Khung nhìn (${percent}%)`,
    fitSlide: "Vừa toàn bộ bảng",
    fitWidth: "Vừa chiều rộng",
    zoom100Label: "Thu phóng 100% (1:1)",
    pageNumberInputAria: "Nhập số trang",
  },
  permissions: {
    teacherDialogTitle: "Cấu hình tương tác của học viên",
    teacherScreenSection: "Màn hình của học viên",
    followYouBtn: "Theo màn bạn",
    freeMoveBtn: "Tự do di chuyển",
    teacherFollowHint:
      "Học viên luôn thấy đúng vùng bảng bạn đang xem, học viên không thể di chuyển sang vị trí khác.",
    teacherFreeHint: "Học viên tự kéo và phóng to để xem bảng.",
    drawingPermissionSection: "Quyền vẽ của học viên",
    viewOnlyMode: "Chỉ xem",
    customMode: "Chọn học viên",
    viewOnlyHint:
      "Cho phép học viên xem / tương tác với bảng. Ngoài ra, bạn có thể chỉ định học viên nào có thể tương tác bảng.",
    customModeHint: "Chọn học viên có thể vẽ được.",
    searchParticipantPlaceholder: "Tìm người tham gia",
    selectedCount: (editable: number, total: number) => `Đã chọn: ${editable}/${total}`,
    deselectAllBtn: "Bỏ chọn",
    selectStudentAria: (name: string) => `Chọn ${name}`,
  },
  pinyinModal: {
    title: "Trợ lý Pinyin & Thẻ Chữ Hán",
    subtitle:
      "Gõ Pinyin kèm số thanh điệu (vd: ni3 hao3, lv4) để tự động chuyển dấu thanh và tạo thẻ ô Điền tự cách trên bảng.",
    modeCardLabel: "Thẻ chữ Hán + Pinyin (田字格)",
    modeCardDesc: "Tạo nhóm ô luyện chữ Điền tự cách / Mễ tự cách kèm chữ Hán và Pinyin phía trên",
    modeTextLabel: "Chỉ chèn văn bản Pinyin / Chữ Hán",
    modeTextDesc: "Chèn trực tiếp dòng văn bản Pinyin và chữ Hán vào giữa màn hình",
    pinyinInputLabel: "Pinyin (hỗ trợ gõ số 1-5 hoặc bấm chọn nguyên âm bên dưới)",
    pinyinInputPlaceholder: "Ví dụ: ni3 hao3, zhong1 wen2, lv4 se4...",
    convertedPreviewLabel: "Pinyin chuẩn hóa dấu thanh",
    hanziInputLabel: "Chữ Hán (mỗi chữ 1 ô)",
    hanziInputPlaceholder: "Ví dụ: 你好, 中文...",
    gridStyleLabel: "Kiểu ô luyện chữ",
    gridTianzige: "Điền tự cách (田字格)",
    gridMizige: "Mễ tự cách (米字格)",
    toneVowelsTitle: "Nguyên âm mang thanh điệu (Nhấn để chèn)",
    punctuationTitle: "Dấu câu tiếng Trung",
    colorLabel: "Màu sắc",
    strokeColorLabels: {
      ink: "Mực đen",
      white: "Trắng",
      crimson: "Đỏ thẫm",
      blue: "Xanh lam",
      emerald: "Xanh lục",
      amber: "Vàng hổ phách",
    },
    copyPinyinButton: "Sao chép Pinyin",
    copiedPinyinButton: "Đã chép!",
    cancelButton: "Hủy",
    insertCardButton: "Chèn thẻ vào bảng",
    insertTextButton: "Chèn văn bản vào bảng",
    practiceTitle: "Chế độ bài tập trên bảng",
    practiceFull: "Mẫu chuẩn (Đủ Pinyin + Hán)",
    practiceTrace: "Tập tô nét (Mẫu + Ô tô mờ)",
    practiceDictation: "Nghe viết (Chỉ hiện Pinyin)",
    practicePinyinQuiz: "Điền Pinyin (Chỉ hiện chữ Hán)",
    gridJiugongge: "Cửu cung cách (九宫格)",
    gridSikexian: "Kẻ 4 dòng Pinyin (四线格)",
    presetsTitle: "Từ vựng HSK, Nét cơ bản & Bộ thủ chọn nhanh",
    cellsCountBadge: (count: number) => `${count} ô`,
  },
  modals: {
    cancel: "Hủy",
    close: "Đóng",
    confirm: "Xác nhận",
    latexEditTitle: "Chỉnh sửa công thức Toán học (LaTeX)",
    latexCloseTitle: "Đóng hộp thoại (Esc)",
    latexChipInsertTitle: (latex: string) => `Chèn ${latex}`,
    latexChips: {
      systemCases: "Hệ phương trình",
      orBrackets: "Hoặc (ngoặc vuông)",
      derivativePrime: "Đạo hàm f'(x)",
      derivative: "Vi phân d/dx",
      partialDerivative: "Đạo hàm riêng",
      integral: "Tích phân",
      limit: "Giới hạn",
      sum: "Tổng Sigma",
      matrix2x2: "Ma trận 2x2",
      iff: "⇔ Tương đương",
      implies: "⇒ Suy ra",
      inSet: "∈ Thuộc",
      realSet: "ℝ Tập số thực",
      vector: "Vectơ AB",
      combination: "Tổ hợp C_n^k",
      subscript: "Chỉ số dưới",
      delta: "Δ Delta",
      alpha: "α Alpha",
      beta: "β Beta",
      lambda: "λ Lambda",
      plusMinus: "± Cộng trừ",
      approx: "≈ Xấp xỉ",
      infinity: "∞ Vô cực",
    },
    latexInkColors: {
      defaultDark: "Mặc định (Trắng thích ứng)",
      defaultLight: "Mặc định (Đen)",
      amber: "Vàng hổ phách",
      blue: "Xanh lam",
      emerald: "Xanh lục",
      rose: "Đỏ hồng",
      purple: "Tím",
    },
    latexInputPlaceholder: "Ví dụ: \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}",
    latexSyntaxWarning: "Cảnh báo cú pháp",
    latexEmptyPreviewHint: "Nhập công thức LaTeX ở trên để xem trước",
    latexInkColorLabel: "Màu mực:",
    latexInsertingButton: "Đang chèn...",
    latexUpdateButton: "Cập nhật công thức",
    latexInsertButton: "Chèn công thức",
    simulationTitle: "Bảng Đồ thị & Hình học",
    simulationSubtitle: "Chọn bảng tọa độ đồ thị hoặc bảng vẽ hình học để thêm vào trang giảng dạy",
    simulationInsertButton: "Chèn vào bảng",
    studios: {
      graphing: {
        title: "Đồ thị hàm số & Giải tích",
        subtitle: "Hệ trục tọa độ Oxy",
        description:
          "Vẽ đồ thị hàm số, miền nghiệm bất phương trình, đường tròn và elip trên mặt phẳng Oxy. Hỗ trợ thanh trượt tham số m, tiếp tuyến, tích phân và tìm nghiệm, cực trị.",
        tags: ["Đồ thị hàm số", "Bất phương trình", "Tham số m", "Tiếp tuyến", "Cực trị & Nghiệm"],
      },
      geometry: {
        title: "Hình học phẳng",
        subtitle: "Dựng hình & kéo thả trực quan",
        description:
          "Bảng vẽ hình học phẳng với các công cụ dựng điểm, đoạn thẳng, đường thẳng vuông góc/song song, đường tròn, đa giác và các mẫu hình chuẩn SGK.",
        tags: ["Điểm & Đoạn thẳng", "Vuông góc & Song song", "Đường tròn", "Đa giác", "Hình mẫu SGK"],
      },
    },
    youtubeTitle: "Chèn Video YouTube",
    youtubeSubtitle: "Nhúng video YouTube với tính năng đồng bộ phát lớp học",
    youtubeUrlLabel: "Đường dẫn YouTube",
    youtubeUrlPlaceholder: "https://www.youtube.com/watch?v=... hoặc https://youtu.be/...",
    youtubeInvalidUrlError: "Đường link không hợp lệ. Hỗ trợ định dạng watch, youtu.be, shorts, embed.",
    youtubeDisplayTitleLabel: "Tiêu đề hiển thị",
    youtubePreviewLabel: "Xem trước (16:9)",
    youtubeStartTimeBadge: (seconds: number) => `Bắt đầu: ${seconds}s`,
    youtubeInsertButton: "Chèn vào bảng",
    videoUploadTitle: "Tải lên Video Bài giảng",
    videoUnsupportedError: "Chỉ hỗ trợ video MP4, WebM (tối đa 250MB)",
    videoPresignFailedError: (status: number) => `Không thể khởi tạo tải lên (${status})`,
    videoUploadHttpFailedError: (status: number) => `Tải lên MinIO thất bại (HTTP ${status})`,
    videoUploadNetworkError: "Lỗi mạng khi tải video lên MinIO",
    mermaidTitle: "Chuyển Mermaid thành Sơ đồ",
    mermaidSubtitle: "Tạo lưu đồ, biểu đồ tuần tự hoặc cấu trúc trực tiếp vào bảng vẽ",
    mermaidCloseAria: "Đóng",
    mermaidTemplatesLabel: "Mẫu:",
    mermaidTemplateFlowchart: "Lưu đồ (Flowchart)",
    mermaidTemplateSequence: "Tuần tự (Sequence)",
    mermaidTemplateClass: "Lớp đối tượng (Class)",
    mermaidSyntaxLabel: "Cú pháp Mermaid:",
    mermaidKeyboardHint: "Ctrl + Enter để chèn",
    mermaidEmptyError: "Vui lòng nhập cú pháp Mermaid.",
    mermaidNoElementsError: "Không thể tạo sơ đồ từ cú pháp này.",
    mermaidSyntaxError: (msg: string) => `Lỗi cú pháp Mermaid: ${msg}`,
    mermaidSupportedDiagramsHint: "Hỗ trợ Flowchart, Sequence, Class, State diagrams",
    mermaidCancelButton: "Hủy",
    mermaidProcessingButton: "Đang xử lý...",
    mermaidInsertButton: "Chèn vào bảng",
    webEmbedTitle: "Nhúng trang web học tập",
    webEmbedSubtitle: "Nhúng nội dung trực quan hoặc mô phỏng vào bài giảng",
    webEmbedCloseAria: "Đóng",
    webEmbedUrlLabel: "Địa chỉ liên kết (URL https://...)",
    webEmbedUrlPlaceholder: "https://example.com/mo-phong",
    webEmbedNameLabel: "Tên hiển thị (tùy chọn)",
    webEmbedNamePlaceholder: "Nội dung nhúng",
    webEmbedInvalidUrlError: "Đường dẫn không hợp lệ. Vui lòng nhập URL bắt đầu bằng https://",
    webEmbedCancelButton: "Hủy",
    webEmbedInsertButton: "Chèn vào bảng",
    mathDockPlotToGraphTitle: "Vẽ biểu thức này lên bảng đồ thị (tự tạo nếu chưa có)",
    mathDockPlotToGraphButton: "Vẽ Đồ thị",
    mathDockStampToBoardTitle: "Dán công thức 2D này ra Bảng trắng",
    mathDockStampToBoardButton: "Dán Bảng",
    mathDockCollapseTitle: "Thu gọn về thanh tối giản",
    mathDockExpandTitle: "Mở rộng bàn phím đầy đủ & kho mẫu SGK",
    mathDockCollapseButton: "Thu gọn",
    mathDockExpandButton: "Mở rộng",
    mathDockDoneButton: "Xong",
    mathDockTabAlgebra: "123 Đại số & Số",
    mathDockTabFunctions: "f(x) Lượng giác / Mũ",
    mathDockTabPresets: "★ Mẫu SGK (1-Click)",
    mathDockTabCalculus: "∫∑ Giải tích & LaTeX",
    mathDockTemplates: {
      cubic_m: "Hàm bậc 3 (Khảo sát & Tham số m)",
      biquadratic_m: "Hàm trùng phương",
      rational: "Hàm phân thức hữu tỉ (Tiệm cận)",
      parabola_m: "Parabol bậc 2",
      trig_wave: "Hàm lượng giác",
      radical: "Hàm căn thức",
      linear_ineq: "Miền nghiệm BPT bậc nhất 2 ẩn",
      circle: "Phương trình Đường tròn",
      ellipse: "Phương trình Elip chính tắc",
    },
  },
  widgets: {
    modeToggleToAnnotateTitle: "Chuyển sang chế độ vẽ ghi chú",
    modeToggleToInteractTitle: "Chuyển sang chế độ tương tác",
    interactLabel: "Tương tác",
    annotateLabel: "Vẽ đè",
    unlockWidgetTitle: "Bảng đang khóa (Bấm để mở khóa)",
    lockWidgetTitle: "Khóa vị trí bảng",
    lockedByTeacherTitle: "Bảng đang được khóa bởi giáo viên",
    maximizeWidgetTitle: "Phóng to vừa màn hình",
    restoreWidgetTitle: "Thu nhỏ về góc nhìn trước",
    confirmDeleteWidgetTitle: "Xác nhận xóa bảng này",
    confirmDeleteWidgetLabel: "Xác nhận xóa?",
    cancelDeleteWidgetTitle: "Hủy xóa",
    cancelDeleteWidgetLabel: "Hủy",
    deleteWidgetAria: "Xóa bảng",
    resizeHandleTitle: "Kéo để thay đổi kích thước",
    microPromptTouchPoint: "1. Chạm cạnh chuẩn hoặc vị trí điểm đi qua",
    microPromptPassThroughPoint: "2. Chạm điểm hoặc vị trí ngoài cạnh để kẻ đường đi qua",
    microPromptPerpendicularEdge: "2. Chạm cạnh để lấy phương vuông góc",
    microPromptParallelEdge: "2. Chạm cạnh để lấy phương song song",
    microPromptCancelTitle: "Hủy thao tác (Esc)",
    openSlidersTitle: "Mở bảng điều khiển thanh trượt tham số",
    parametersLabel: "Tham số",
    collapseSlidersTitle: "Thu gọn bảng tham số",
    collapseLabel: "Thu gọn",
    videoIndependentTitle: "Đang ở chế độ Tự xem độc lập - Bấm để đồng bộ theo giáo viên",
    videoFollowingTitle: "Đang đồng bộ theo giáo viên - Bấm để tự xem độc lập",
    videoIndependentLabel: "Tự xem",
    videoFollowTeacherLabel: "Theo giáo viên",
    videoResyncButton: "Quay lại theo giáo viên",
    videoResyncTitle: "Quay lại phát đồng bộ theo giáo viên",
    hidePlotTitle: "Ẩn đồ thị",
    showPlotTitle: "Hiện đồ thị",
    hideInequalityTitle: "Ẩn miền nghiệm",
    showInequalityTitle: "Hiện miền nghiệm",
    hideConicTitle: "Ẩn conic",
    showConicTitle: "Hiện conic",
    inequalityShortTag: "bpt:",
    deleteExpressionTitle: (label: string) => `Xóa ${label}`,
    invalidSyntaxLabel: "Cú pháp không hợp lệ",
    mobileExpressionListTitle: "Danh sách biểu thức & Giải tích",
    addButtonLabel: "Thêm",
    openMobileFormulaSheetTitle: "Bấm để mở bảng công thức & bàn phím cảm ứng iPad",
    emptyExpressionLabel: "(trống)",
    openAlgebraDrawerTitle: "Mở bảng đại số (Danh sách biểu thức)",
    expressionListTitle: "Danh sách biểu thức",
    collapseAlgebraDrawerTitle: "Thu gọn bảng đại số [ ◀ ]",
    omniInputPlaceholder: "Nhập hàm số, bất phương trình, đường tròn/elip...",
    omniInputAria: "Nhập biểu thức mới",
    clickToAddExpression: "Bấm để nhập hàm số / BPT...",
    newBadge: "+ Mới",
    addPointLabel: "+ Điểm",
    triangleAbcLabel: "Tam giác ABC",
    triangleLabel: "Tam giác",
    segmentLabel: "Đoạn thẳng",
    lineLabel: "Đường thẳng",
    vectorLabel: "Vectơ",
    perpendicularLabel: "Vuông góc",
    parallelLabel: "Song song",
    circleLabel: "Đường tròn",
    polygonLabel: "Đa giác",
    clearGeometryBoardTitle: "Xóa toàn bộ hình",
    geometryPresets: {
      tri_altitudes: "Tam giác & 3 đường cao",
      tri_medians: "Tam giác & 3 trung tuyến",
      tri_circum: "Tam giác & Đ/tròn ngoại tiếp",
      tri_in: "Tam giác & Đ/tròn nội tiếp",
      tri_right: "Tam giác vuông",
      tri_iso: "Tam giác cân",
      tri_eq: "Tam giác đều",
      quad_para: "Hình bình hành",
      quad_rect: "Hình chữ nhật",
      quad_rhomb: "Hình thoi",
      quad_sq: "Hình vuông",
      circle_tan: "Đường tròn & 2 tiếp tuyến",
      quad_inscribed: "Tứ giác nội tiếp",
      poly_pentagon: "Ngũ giác đều",
      quad_trapezoid: "Hình thang cân",
    },
    extremumMaxBadge: (x: string, y: string) => `CĐ(${x}, ${y})`,
    extremumMinBadge: (x: string, y: string) => `CT(${x}, ${y})`,
    rootSweeperBadge: (paramName: string, val: string, count: number) =>
      `${paramName} = ${val} → ${count} nghiệm`,
    lpUnboundedReasons: {
      both_unbounded: "Miền nghiệm không bị chặn - Không tồn tại GTLN/GTNN",
      max_unbounded: "Không tồn tại GTLN (tiến tới +∞ do miền nghiệm không bị chặn)",
      min_unbounded: "Không tồn tại GTNN (tiến tới -∞ do miền nghiệm không bị chặn)",
    },
    lpTableHeader: (fnExpr: string) => `Tối ưu F(x, y) = ${fnExpr}`,
    lpVertexCol: "Đỉnh",
    lpCoordsCol: "Tọa độ",
    lpMaxSummary: (val: string, label: string, x: string, y: string) =>
      `★ max F = ${val} tại ${label} (${x}, ${y})`,
    lpMinSummary: (val: string, label: string, x: string, y: string) =>
      `★ min F = ${val} tại ${label} (${x}, ${y})`,
    stopwatchPauseTitle: "Tạm dừng mô phỏng",
    stopwatchPlayTitle: "Chạy mô phỏng",
    stopwatchResetTitle: "Đặt lại thời gian",
    riemannMethodLeft: "Trái",
    riemannMethodMidpoint: "Giữa",
    riemannMethodRight: "Phải",
    riemannMethodTrapezoidal: "Hình thang",
    riemannExpandTitle: "Bấm để mở rộng bảng điều khiển Tổng Riemann",
    riemannCollapseTitle: "Thu gọn bảng điều khiển Riemann",
    riemannCloseTitle: "Đóng Tổng Riemann",
    riemannFullTitle: "Tổng Riemann",
    riemannDec5Title: "Giảm 5 phân hoạch",
    riemannDec1Title: "Giảm 1 phân hoạch",
    riemannInputTitle: "Nhập trực tiếp số phân hoạch n (2 - 100)",
    riemannInc1Title: "Tăng 1 phân hoạch",
    riemannInc5Title: "Tăng 5 phân hoạch",
    sweeperExpandTitle: "Bấm để mở rộng thanh điều khiển thước quét y = m",
    sweeperCollapseTitle: "Thu gọn thanh tương giao y = m",
    sweeperCloseTitle: "Đóng tương giao y = m",
    sweeperFullTitle: "Tương giao y = m",
    sweeperInputTitle: "Nhập trực tiếp giá trị m",
    sweeperDec05Title: "Giảm 0.5",
    sweeperDec01Title: "Giảm 0.1",
    sweeperResetZeroTitle: "Đặt về 0",
    sweeperInc01Title: "Tăng 0.1",
    sweeperInc05Title: "Tăng 0.5",
    insituCircumcircleLabel: "Ngoại tiếp",
    insituIncircleLabel: "Nội tiếp",
    insituAltitudesLabel: "Đường cao",
    insituMediansLabel: "Trung tuyến",
    insituBisectorsLabel: "Phân giác",
    insituMeasurementsLabel: "Số đo",
    insituMidpointLabel: "Trung điểm M",
    insituBisectorLabel: "Trung trực d",
    insituLengthLabel: "Độ dài",
    insituCircleHeader: "Đường tròn",
    insituTwoTangentsLabel: (ptId: string) => `2 Tiếp tuyến (${ptId})`,
    insituCreateTangentsLabel: "2 Tiếp tuyến",
    insituPointLabelPrefix: "Điểm:",
    insituVertexAltitudeTitle: "Đường cao từ đỉnh này",
    insituVertexMedianTitle: "Trung tuyến từ đỉnh này",
    insituVertexBisectorTitle: "Phân giác từ đỉnh này",
    insituPolygonHeader: (count: number) => `Đa giác (${count} đỉnh)`,
    insituDeleteElementTitle: "Xóa phần tử",
    insituCloseTitle: "Đóng",
    dockCompactTriggerTitle: "Mở danh sách đặc trưng sư phạm",
    dockCompactTriggerLabel: "Đặc trưng",
    dockMenuHeader: "Đặc trưng sư phạm",
    dockMoreActionsTitle: "Xem tất cả các hành động khác",
    dockMoreActionsLabel: (count: number) => `Thêm (${count})`,
    dockAllActionsHeader: "Tất cả hành động",
    tangentOutOfDomainBadge: (x0: string) => `x₀ = ${x0} (ngoài TXĐ)`,
    unmuteVideoLabel: "Bật âm thanh",
    snapIsoscelesBadge: "Cân",
    emptyFeasibleRegionNotice: "Miền nghiệm rỗng",
    feasiblePointMemberBadge: (objValueStr: string) => `✓ Thuộc miền nghiệm${objValueStr}`,
    feasiblePointViolatedBadge: (expr: string) => `✕ Vi phạm: ${expr}`,
    pedagogicalActions: {
      hatchingLabel: "Gạch miền sai SGK",
      hatchingTitle: "Biểu diễn miền nghiệm bất phương trình theo SGK",
      objectiveOptimizerLabel: "Tối ưu hàm F",
      objectiveOptimizerTitle: "Tìm giá trị lớn nhất, nhỏ nhất của hàm mục tiêu F(x, y)",
      curveIntersectionsLabel: (plotName: string, count: number) =>
        count > 0 ? `Giao điểm của ${plotName} (${count})` : `Giao điểm của ${plotName}`,
      curveIntersectionsTitle: (plotName: string) =>
        `Tìm giao điểm và dóng trục toạ độ của đồ thị ${plotName} với các đồ thị khác`,
      areaBetweenCurvesLabel: "Diện tích 2 đồ thị",
      areaBetweenCurvesTitle: "Diện tích hình phẳng giới hạn bởi 2 đồ thị f(x) và g(x)",
      conicAxesLabel: (isEllipse: boolean) =>
        isEllipse ? "Hiện Tâm & Đỉnh/Trục" : "Hiện Tâm & Bán kính",
      conicAxesTitle: "Hiển thị tâm, các đỉnh và trục của đường conic",
      conicFociLabel: "Hiện Tiêu điểm",
      conicFociTitle: "Hiển thị tiêu điểm F₁, F₂ và tiêu cự của Elip",
      asymptotesLabel: "Hiện Tiệm cận",
      asymptotesTitle: "Hiển thị đường tiệm cận đứng và tiệm cận ngang",
      symmetryCenterLabel: "Tâm đối xứng",
      symmetryCenterTitle: "Tọa độ tâm đối xứng của đồ thị",
      quadraticVertexAxisLabel: "Hiện Đỉnh & Trục",
      quadraticVertexAxisTitle: "Tọa độ đỉnh và trục đối xứng của Parabol",
      monotonicityLabel: "Biến thiên & Cực trị",
      monotonicityTitle: "Bảng biến thiên và các điểm cực trị",
      inflectionPointLabel: "Điểm uốn",
      inflectionPointTitle: "Tọa độ điểm uốn của đồ thị hàm bậc ba",
      keyFeaturesLabel: "Điểm đặc trưng",
      keyFeaturesTitle: "Các điểm đặc trưng trên đồ thị",
      curveProbeLabel: "Dóng trục điểm M",
      curveProbeTitle: "Điểm di động M trên đồ thị với dóng trục toạ độ Ox, Oy",
      intervalAnalysisLabel: "Khảo sát đoạn [a, b]",
      intervalAnalysisTitle: "Khảo sát giá trị lớn nhất, nhỏ nhất trên đoạn [a, b] với con trỏ kéo thả",
      rootSweeperLabel: "Tương giao y = m",
      rootSweeperTitle: "Biện luận số nghiệm phương trình f(x) = m bằng tương giao đồ thị",
      tangentLineLabel: "Tiếp tuyến tại x₀",
      tangentLineTitle: "Viết phương trình tiếp tuyến của đồ thị tại điểm x₀",
      riemannSumLabel: "Tổng Riemann",
      riemannSumTitle: "Xấp xỉ diện tích hình phẳng bằng tổng Riemann tích phân",
      definiteIntegralLabel: "Tích phân xác định",
      definiteIntegralTitle: "Tính tích phân xác định và diện tích hình phẳng S",
      absFTransformLabel: "Lật đồ thị |f(x)|",
      absFTransformTitle: "Biến đổi đồ thị giữ nguyên phần trên Ox và lấy đối xứng phần dưới",
      fAbsTransformLabel: "Đối xứng f(|x|)",
      fAbsTransformTitle: "Biến đổi đồ thị giữ nguyên phần bên phải Oy và lấy đối xứng sang trái",
    },
  },
};
